import VueApexCharts from 'vue-apexcharts';
// eslint-disable-next-line import/no-extraneous-dependencies
import { debounce } from 'lodash';
import { CVSSv3 } from '../../../assets/libs/3rd-party/cvss3';
import { CVSS31 } from '../../../assets/libs/3rd-party/cvss31';

const CVSS_V31_VERSION_STRING = '3.1';

const NALP = {
    N: 'Network',
    A: 'Adjacent',
    L: 'Local',
    P: 'Physical',
};

const NLH = {
    N: 'None',
    L: 'Low',
    H: 'High',
    U: 'Unchanged',
    C: 'Changed',
    R: 'Required',
};


const RADIAL_V3_MAPPING = {
    AV: {
        N: 5,
        A: 4,
        L: 3,
        P: 2,
    },
    AC: {
        L: 4,
        H: 2,
    },
    PR: {
        N: 5,
        L: 3,
        H: 2,
    },
    UI: {
        N: 4,
        R: 2,
    },
    S: {
        C: 4,
        U: 2,
    },
    C: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    I: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    A: {
        H: {
            H: 5,
            M: 4,
            L: 3,
            X: 4,
        },
        L: {
            H: 3,
            M: 2,
            L: 1,
            X: 2,
        },
        N: {
            H: 0,
            M: 0,
            L: 0,
            X: 0,
        },
        X: {
            H: 4,
            M: 4,
            L: 3,
            X: 2,
        },
    },
    E: {
        H: 5,
        F: 4,
        P: 3,
        U: 2,
        X: 3,
    },
    RL: {
        U: 5,
        W: 4,
        T: 3,
        O: 2,
        X: 3,
    },
    RC: {
        C: 5,
        R: 3,
        U: 2,
        X: 3,
    },
};

export const CSSV3BaseSection = {
    props: {
        cvssVector: {
            type: String,
            default: null,
        },
        cvssAV: {
            type: String,
            default: null,
        },
        cvssAC: {
            type: String,
            default: null,
        },
        cvssPR: {
            type: String,
            default: null,
        },
        cvssUI: {
            type: String,
            default: null,
        },
        cvssS: {
            type: String,
            default: null,
        },
        cvssC: {
            type: String,
            default: null,
        },
        cvssI: {
            type: String,
            default: null,
        },
        cvssA: {
            type: String,
            default: null,
        },
        cvssE: {
            type: String,
            default: 'X',
        },
        cvssRL: {
            type: String,
            default: 'X',
        },
        cvssRC: {
            type: String,
            default: 'X',
        },
        cvssCR: {
            type: String,
            default: 'X',
        },
        cvssIR: {
            type: String,
            default: 'X',
        },
        cvssAR: {
            type: String,
            default: 'X',
        },
        cvssMAV: {
            type: String,
            default: 'X',
        },
        cvssMAC: {
            type: String,
            default: 'X',
        },
        cvssMPR: {
            type: String,
            default: 'X',
        },
        cvssMUI: {
            type: String,
            default: 'X',
        },
        cvssMS: {
            type: String,
            default: 'X',
        },
        cvssMC: {
            type: String,
            default: 'X',
        },
        cvssMI: {
            type: String,
            default: 'X',
        },
        cvssMA: {
            type: String,
            default: 'X',
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        apexchart: VueApexCharts,
    },
    data() {
        return {
            badgeClass: 'badge badge-cvss',
            score: null,
            baseScore: null,
            temporalScore: null,
            environmentalScore: null,
            vector: this.cvssVector ?? '-',
            AV: this.cvssAV,
            AC: this.cvssAC,
            PR: this.cvssPR,
            UI: this.cvssUI,
            S: this.cvssS,
            C: this.cvssC,
            I: this.cvssI,
            A: this.cvssA,
            E: this.cvssE,
            RL: this.cvssRL,
            RC: this.cvssRC,
            CR: this.cvssCR,
            IR: this.cvssIR,
            AR: this.cvssAR,
            MAV: this.cvssMAV,
            MAC: this.cvssMAC,
            MPR: this.cvssMPR,
            MUI: this.cvssMUI,
            MS: this.cvssMS,
            MC: this.cvssMC,
            MI: this.cvssMI,
            MA: this.cvssMA,
            calculator: CVSSv3,
        };
    },
    computed: {
        NALP: () => NALP,
        NLH: () => NLH,
    },
    methods: {
        checkBase() {
            // returns whether all base values are selected
            return [
                this.AV,
                this.AC,
                this.PR,
                this.UI,
                this.S,
                this.C,
                this.I,
                this.A,
            ].some((e) => !e);
        },
        updateMetrics(
            AV,
            AC,
            PR,
            UI,
            S,
            C,
            I,
            A,
            E,
            RL,
            RC,
            CR,
            IR,
            AR,
            MAV,
            MAC,
            MPR,
            MUI,
            MS,
            MC,
            MI,
            MA,
        ) {
            if (AV) this.AV = AV;
            if (AC) this.AC = AC;
            if (PR) this.PR = PR;
            if (UI) this.UI = UI;
            if (S) this.S = S;
            if (C) this.C = C;
            if (I) this.I = I;
            if (A) this.A = A;
            if (E) this.E = E;
            if (RL) this.RL = RL;
            if (RC) this.RC = RC;
            if (CR) this.CR = CR;
            if (IR) this.IR = IR;
            if (AR) this.AR = AR;
            if (MAV) this.MAV = MAV;
            if (MAC) this.MAC = MAC;
            if (MPR) this.MPR = MPR;
            if (MUI) this.MUI = MUI;
            if (MS) this.MS = MS;
            if (MC) this.MC = MC;
            if (MI) this.MI = MI;
            if (MA) this.MA = MA;
        },
        updateScoreVector(
            baseMetricScore,
            temporalMetricScore,
            environmentalMetricScore,
            vectorString,
        ) {
            this.vector = vectorString;
            this.baseScore = baseMetricScore;
            this.temporalScore = temporalMetricScore;
            this.environmentalScore = environmentalMetricScore;
            this.$emit('update', {
                vector: this.vector,
                baseScore: this.baseScore,
                temporalScore: this.temporalScore,
                environmentalScore: this.environmentalScore,
            });
        },
        calculateScore() {
            try {
                if (this.checkBase()) {
                    // some of the base selects are missing - no update possible
                    return;
                }
                const {
                    success,
                    vectorString,
                    baseMetricScore,
                    temporalMetricScore,
                    environmentalMetricScore,
                } = this.calculator.calculateCVSSFromMetrics(
                    this.AV,
                    this.AC,
                    this.PR,
                    this.UI,
                    this.S,
                    this.C,
                    this.I,
                    this.A,
                    this.E,
                    this.RL,
                    this.RC,
                    this.CR,
                    this.IR,
                    this.AR,
                    this.MAV,
                    this.MAC,
                    this.MPR,
                    this.MUI,
                    this.MS,
                    this.MC,
                    this.MI,
                    this.MA,
                );
                if (success) {
                    this.updateScoreVector(
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        },
        updateScoreFromVector(vector) {
            try {
                const {
                    success,
                    AV,
                    AC,
                    PR,
                    UI,
                    S,
                    C,
                    I,
                    A,
                    E,
                    RL,
                    RC,
                    CR,
                    IR,
                    AR,
                    MAV,
                    MAC,
                    MPR,
                    MUI,
                    MS,
                    MC,
                    MI,
                    MA,
                    vectorString,
                    baseMetricScore,
                    temporalMetricScore,
                    environmentalMetricScore,
                } = this.calculator.calculateCVSSFromVector(vector);
                if (success) {
                    this.updateMetrics(
                        AV,
                        AC,
                        PR,
                        UI,
                        S,
                        C,
                        I,
                        A,
                        E,
                        RL,
                        RC,
                        CR,
                        IR,
                        AR,
                        MAV,
                        MAC,
                        MPR,
                        MUI,
                        MS,
                        MC,
                        MI,
                        MA,
                    );
                    this.updateScoreVector(
                        baseMetricScore,
                        temporalMetricScore,
                        environmentalMetricScore,
                        vectorString,
                    );
                }
            } catch (e) {
                //
            }
        },
        handleVectorChange: debounce(function handler(event) {
            try {
                const vector = event?.target?.value;
                this.calculator = vector.includes(CVSS_V31_VERSION_STRING)
                    ? CVSS31
                    : CVSSv3;
                if (vector) {
                    this.updateScoreFromVector(vector);
                }
            } catch (e) {
                //
            }
        }, 50),
    },
    watch: {
        vector() {
            this.handleVectorChange();
        },
    },
    mounted() {
        if (this.vector) {
            this.calculator = this.vector.includes(CVSS_V31_VERSION_STRING)
                ? CVSS31
                : CVSSv3;
            this.updateScoreFromVector(this.vector);
        }
    },
    template: `
        <div>
                <dt>Base Score</dt>
                <div class="d-flex flex-column">
                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" title="This metric reflects the context by which vulnerability exploitation is possible. The Base Score increases the more remote (logically, and physically) an attacker can be in order to exploit the vulnerable component.">Attack Vector (AV)</span>
                        <span class="tooltip-bordered" v-if="AV === 'N'" name="CVSS_V3_AV" value="N" id="CVSS_v3_AV_N" title="The vulnerable component is bound to the network stack and the set of possible attackers extends beyond the other options listed, up to and including the entire Internet. Such a vulnerability is often termed 'remotely exploitable' and can be thought of as an attack being exploitable at the protocol level one or more network hops away (e.g., across one or more routers).">Network</span>
                        <span class="tooltip-bordered" v-if="AV === 'A'" name="CVSS_V3_AV" value="A" id="CVSS_v3_AV_A" title="The vulnerable component is bound to the network stack, but the attack is limited at the protocol level to a logically adjacent topology. This can mean an attack must be launched from the same shared physical (e.g., Bluetooth or IEEE 802.11) or logical (e.g., local IP subnet) network, or from within a secure or otherwise limited administrative domain (e.g., MPLS, secure VPN to an administrative network zone).">
                            Adjacent</span>
                        <span class="tooltip-bordered" v-if="AV === 'L'" name="CVSS_V3_AV" value="L" id="CVSS_v3_AV_L" title="The vulnerable component is not bound to the network stack and the attacker’s path is via read/write/execute capabilities. Either: the attacker exploits the vulnerability by accessing the target system locally (e.g., keyboard, console), or remotely (e.g., SSH); or the attacker relies on User Interaction by another person to perform actions required to exploit the vulnerability (e.g., tricking a legitimate user into opening a malicious document).">
                            Local</span>
                        <span class="tooltip-bordered" v-if="AV === 'P'" name="CVSS_V3_AV" value="P" id="CVSS_v3_AV_P" title="The attack requires the attacker to physically touch or manipulate the vulnerable component. Physical interaction may be brief or persistent.">
                            Physical</span>
                    </div>
                    
                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_AC_Heading"
                            title="This metric describes the conditions beyond the attacker’s control that must exist in order to exploit the vulnerability. Such conditions may require the collection of more information about the target or computational exceptions. The assessment of this metric excludes any requirements for user interaction in order to exploit the vulnerability. If a specific configuration is required for an attack to succeed, the Base metrics should be scored assuming the vulnerable component is in that configuration.">
                            Attack Complexity (AC)</span>
                        <span class="tooltip-bordered" v-if="AC === 'L'" title="Specialized access conditions or extenuating circumstances do not exist. An attacker can expect repeatable success against the vulnerable component." name="CVSS_V3_AC" value="L" id="CVSS_v3_AC_L">Low
                            </span>
                        <span class="tooltip-bordered" v-if="AC === 'H'" title="A successful attack depends on conditions beyond the attacker's control. That is, a successful attack cannot be accomplished at will, but requires the attacker to invest in some measurable amount of effort in preparation or execution against the vulnerable component before a successful attack can be expected. For example, a successful attack may require an attacker to: gather knowledge about the environment in which the vulnerable target/component exists; prepare the target environment to improve exploit reliability; or inject themselves into the logical network path between the target and the resource requested by the victim in order to read and/or modify network communications (e.g., a man in the middle attack)." name="CVSS_V3_AC" value="H" id="CVSS_v3_AC_H">High
                            </span>
                    </div>

                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_PR_Heading"
                            title="This metric describes the level of privileges an attacker must possess before successfully exploiting the vulnerability.">
                            Privileges Required (PR)</span>
                        <span class="tooltip-bordered" v-if="PR === 'N'" title="The attacker is unauthorized prior to attack, and therefore does not require any access to settings or files to carry out an attack." name="CVSS_V3_PR" value="N" id="CVSS_v3_PR_N">
                            None</span>
                        <span class="tooltip-bordered" v-if="PR === 'L'" title="The attacker is authorized with (i.e., requires) privileges that provide basic user capabilities that could normally affect only settings and files owned by a user. Alternatively, an attacker with Low privileges may have the ability to cause an impact only to non-sensitive resources." name="CVSS_V3_PR" value="L" id="CVSS_v3_PR_L">
                            Low</span>
                        <span class="tooltip-bordered" v-if="PR === 'H'" title="The attacker is authorized with (i.e., requires) privileges that provide significant (e.g., administrative) control over the vulnerable component that could affect component-wide settings and files." name="CVSS_V3_PR" value="H" id="CVSS_v3_PR_H">
                            High</span>
                    </div>

                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_UI_Heading"
                            title="This metric captures the requirement for a user, other than the attacker, to participate in the successful compromise the vulnerable component. This metric determines whether the vulnerability can be exploited solely at the will of the attacker, or whether a separate user (or user-initiated process) must participate in some manner.">
                            User Interaction (UI)</span>
                        <span class="tooltip-bordered" v-if="UI === 'N'" title="The vulnerable system can be exploited without any interaction from any user." name="CVSS_V3_UI" value="N" id="CVSS_v3_UI_N" >
                            None</span>
                        <span class="tooltip-bordered" v-if="UI === 'R'" title="Successful exploitation of this vulnerability requires a user to take some action before the vulnerability can be exploited." name="CVSS_V3_UI" value="R" id="CVSS_v3_UI_R" >
                            Required</span>
                    </div>

                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_S_Heading"
                            title="Does a successful attack impact a component other than the vulnerable component? If so, the Base Score increases and the Confidentiality, Integrity and Authentication metrics should be scored relative to the impacted component.">
                            Scope (S)</span>
                        <span class="tooltip-bordered" v-if="S === 'U'" title="An exploited vulnerability can only affect resources managed by the same security authority. In this case, the vulnerable component and the impacted component are either the same, or both are managed by the same security authority." name="CVSS_V3_S" value="U" id="CVSS_v3_S_U">
                            Unchanged</span>
                        <span class="tooltip-bordered" v-if="S === 'C'" title="An exploited vulnerability can affect resources beyond the security scope managed by the security authority of the vulnerable component. In this case, the vulnerable component and the impacted component are different and managed by different security authorities." name="CVSS_V3_S" value="C" id="CVSS_v3_S_C" >
                            Changed
                        </span>
                    </div>
                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_C_Heading"
                            title="This metric measures the impact to the confidentiality of the information resources managed by a software component due to a successfully exploited vulnerability. Confidentiality refers to limiting information access and disclosure to only authorized users, as well as preventing access by, or disclosure to, unauthorized ones.">
                            Confidentiality</span>
                        <span class="tooltip-bordered" v-if="C === 'N'" title="There is no loss of confidentiality within the impacted component." name="CVSS_V3_C" value="N" id="CVSS_v3_C_N">
                            None</span>
                        <span class="tooltip-bordered" v-if="C === 'L'" title="There is some loss of confidentiality. Access to some restricted information is obtained, but the attacker does not have control over what information is obtained, or the amount or kind of loss is limited. The information disclosure does not cause a direct, serious loss to the impacted component." name="CVSS_V3_C" value="L" id="CVSS_v3_C_L">
                            Low</span>
                        <span class="tooltip-bordered" v-if="C === 'H'" title="There is total loss of confidentiality, resulting in all resources within the impacted component being divulged to the attacker. Alternatively, access to only some restricted information is obtained, but the disclosed information presents a direct, serious impact." name="CVSS_V3_C" value="H" id="CVSS_v3_C_H">
                            High</span>
                    </div>
                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_I_Heading"
                            title="This metric measures the impact to integrity of a successfully exploited vulnerability. Integrity refers to the trustworthiness and veracity of information.">
                            Integrity (I)</span>
                        <span class="tooltip-bordered" v-if="I === 'N'" title="There is no loss of integrity within the impacted component." name="CVSS_V3_I" value="N" id="CVSS_v3_I_N">None (N)</span>
                        <span class="tooltip-bordered" v-if="I === 'L'" title="Modification of data is possible, but the attacker does not have control over the consequence of a modification, or the amount of modification is limited. The data modification does not have a direct, serious impact on the impacted component." name="CVSS_V3_I" value="L" id="CVSS_v3_I_L">
                            Low</span>
                        <span class="tooltip-bordered" v-if="I === 'H'" title="There is a total loss of integrity, or a complete loss of protection. For example, the attacker is able to modify any/all files protected by the impacted component. Alternatively, only some files can be modified, but malicious modification would present a direct, serious consequence to the impacted component." name="CVSS_V3_I" value="H" id="CVSS_v3_I_H">
                            High</span>
                    </div>
                    <div class="d-flex justify-content-between p-1">
                        <span class="tooltip-bordered" id="CVSS_v3_A_Heading"
                            title="This metric measures the impact to the availability of the impacted component resulting from a successfully exploited vulnerability. It refers to the loss of availability of the impacted component itself, such as a networked service (e.g., web, database, email). Since availability refers to the accessibility of information resources, attacks that consume network bandwidth, processor cycles, or disk space all impact the availability of an impacted component.">
                            Availability (A)</span>
                        <span class="tooltip-bordered" v-if="A === 'N'" title="There is no impact to availability within the impacted component." name="CVSS_V3_A" value="N" id="CVSS_v3_A_N">
                            None</span>
                        <span class="tooltip-bordered" v-if="A === 'L'" title="Performance is reduced or there are interruptions in resource availability. Even if repeated exploitation of the vulnerability is possible, the attacker does not have the ability to completely deny service to legitimate users. The resources in the impacted component are either partially available all of the time, or fully available only some of the time, but overall there is no direct, serious consequence to the impacted component." name="CVSS_V3_A" value="L" id="CVSS_v3_A_L">
                            Low</span>
                        <span class="tooltip-bordered" v-if="A === 'H'" title="There is total loss of availability, resulting in the attacker being able to fully deny access to resources in the impacted component; this loss is either sustained (while the attacker continues to deliver the attack) or persistent (the condition persists even after the attack has completed). Alternatively, the attacker has the ability to deny some availability, but the loss of availability presents a direct, serious consequence to the impacted component (e.g., the attacker cannot disrupt existing connections, but can prevent new connections; the attacker can repeatedly exploit a vulnerability that, in each instance of a successful attack, leaks a only small amount of memory, but after repeated exploitation causes a service to become completely unavailable)." name="CVSS_V3_A" value="H" id="CVSS_v3_A_H">
                            High</span>
                    </div>
                </div>
        </div>
`,
};
